import { isBrowser } from "../../../../common/helpers/env";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import cn from "classnames";
import { useDispatch } from "react-redux";

import { taskTypes, isMobile } from "../../../../common/parameters";
import moveTask from "../../../store/plan/actions/moveTask";
import removeTask from "../../../store/plan/actions/removeTask";
import changeTaskDescription from "../../../store/plan/actions/changeTaskDescription";
import Icon from "../../../icon";
import routing from "../../../routing";

import Wrapper from "../../../components/Task/Wrapper";
import Tooltip from "../../../components/Tooltip";
import Modal from "../../../components/Modal";
import Form, { Cancel, Title, Submit, InputTextArea } from "../../../components/Form";

import {
    StyledTaskCard,
    ArrowButtonUp,
    ArrowButtonDown,
    StyledIcon,
    TaskPreview,
    EmptyCard,
    Separator,
    QuickButtons,
    TaskTypeWrapper,
    TaskType,
    Alert,
    Generate,
    Description,
} from "./styles";
import messages from "./messages";
import { IProps } from "./interfaces";
import { ISolfanameData } from "../../../../common/types";

const formatCase = (text: string) => {
    return text.slice(0, 1).toUpperCase() + text.slice(1).toLowerCase();
}
const TaskCard = ({
    Task,
    Icon: TypeIcon,
    publicView,
    plan,
    index,
    tasks,
    playable,
    simulation,
    loading,
    moderatorView,
    setLoading,
}: IProps) => {
    const task = tasks[index];
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();
    const cardRef = useRef<HTMLDivElement>(null);
    const [mobileScroll, setMobileScroll] = useState(false);
    const [isHover, setIsHover] = useState(false);
    const [removeTaskModal, setRemoveTaskModal] = useState(false);
    const [editDescription, setEditDescription] = useState(false);
    const [description, setDescription] = useState(task.description || "");

    const handleScroll = () => {
        if (!cardRef.current) {
            return;
        }
        const height = window.innerHeight;
        const { height: cardHeight, top: cardTop } = cardRef.current.getBoundingClientRect();
        if (cardTop > 30 && cardTop + cardHeight + 30 < height) {
            setMobileScroll(true);
        } else {
            setMobileScroll(false);
        }
    };

    useEffect(() => {
        if (isMobile) {
            window.addEventListener("scroll", handleScroll);
        }
        return () => {
            window.removeEventListener("scroll", handleScroll);
        }
    }, [])

    useEffect(() => {
        setTimeout(() => {
            setIsHover(false);
        }, 300);
    }, [index]);
    
    const handleMoveTask = async (index: number) => {
        setLoading(true);
        await dispatch(moveTask({ planId: plan._id, index }));
        setLoading(false);
    };

    const handleRemoveTask = async () => {
        if ((task.data as ISolfanameData).audioShared || (task.data as ISolfanameData).audioShared2) {
            return alert("Please unlink the linked audios first!");
        }
        setLoading(true);
        await dispatch(removeTask({ planId: plan._id, index }));
        setLoading(false);
        setRemoveTaskModal(false);
    };

    const handleChangeDescription = (event: React.FormEvent<HTMLTextAreaElement>) => {
        setDescription(event.currentTarget.value);
    }

    const handleSaveDescription = async () => {
        setLoading(true);
        await dispatch(changeTaskDescription({ planId: plan._id, index, description }));
        setLoading(false);
        setEditDescription(false);
    }

    return (
        <StyledTaskCard
            className={cn((isHover || mobileScroll) && "Hover")}
            ref={cardRef}
            onMouseEnter={() => {
                if (!isMobile) {
                    setIsHover(true);
                }
            }}
            onMouseMove={() => {
                if (!isMobile) {
                    setIsHover(true);
                }
            }}
            onMouseLeave={() => {
                if (!isMobile) {
                    setIsHover(false);
                }
            }}
        >
            <TaskPreview>
                {isBrowser ? (
                    <Task preview task={task} Wrapper={Wrapper} edit publicView={publicView} plan={plan} />
                ) : (
                    <EmptyCard>
                        <svg viewBox="0 0 1280 720"></svg>
                    </EmptyCard>
                )}

                <QuickButtons parentHover={isHover || mobileScroll}>
                    <Tooltip
                        label={publicView ? formatMessage(messages.viewTask) : formatMessage(messages.editTask)}
                        light
                        disabled={loading}
                    >
                        <Link
                            to={`/${
                                moderatorView
                                    ? routing.testModeration
                                    : publicView
                                    ? routing.publicPlan
                                    : routing.plan
                            }/${plan._id}/${routing.tasks}/${index + 1}`}
                            onClick={e => {
                                if (simulation || loading) {
                                    e.preventDefault();
                                }
                            }}
                        >
                            {publicView ? <Icon i="view" /> : <Icon i="edit" />}
                        </Link>
                    </Tooltip>
                    {(!publicView || !!description) && (
                        <Tooltip
                            label={formatMessage(messages.description)}
                            light
                            disabled={loading}
                        >
                            <a
                                onClick={() => {
                                    if (!simulation) {
                                        setEditDescription(true);
                                    }
                                }}
                            >
                                <Icon i="messages" />
                            </a>
                        </Tooltip>
                    )}
                    {!publicView && (
                        <Tooltip label={formatMessage(messages.delete)} light disabled={loading}>
                            <a
                                onClick={() => {
                                    if (!simulation) {
                                        setRemoveTaskModal(true);
                                    }
                                }}
                            >
                                <Icon i="trash" />
                            </a>
                        </Tooltip>
                    )}
                </QuickButtons>
            </TaskPreview>
            <Separator></Separator>
            <StyledIcon>
                <TypeIcon active={isHover || mobileScroll} />
            </StyledIcon>
            <TaskTypeWrapper>
                <TaskType>
                    {index + 1}. {taskTypes(formatMessage)[task.type]}
                    {!publicView && !playable && (
                        <Alert>
                            <Icon i="alert" />
                            <div className={cn("Tooltip")}>
                                <FormattedMessage {...messages.alert} />
                            </div>
                        </Alert>
                    )}
                    {!publicView && task.ref && (
                        <Generate>
                            <Icon i="generate" />
                        </Generate>
                    )}
                    {!!task.description && (
                        <Description className={cn({ NextToGenerate: !publicView && task.ref })}>
                            <Icon i="messages" />
                        </Description>
                    )}
                </TaskType>
            </TaskTypeWrapper>
            {index > 0 && !publicView && (
                <ArrowButtonUp
                    className={cn("button", "button--light", "button--thin", loading && "button--loading")}
                    onClick={() => {
                        if (!loading) {
                            handleMoveTask(index - 1);
                        }
                    }}
                >
                    <Icon i="arrowup" />
                </ArrowButtonUp>
            )}{" "}
            {index < tasks.length - 1 && !publicView && (
                <ArrowButtonDown
                    className={cn("button", "button--light", "button--thin", loading && "button--loading")}
                    onClick={() => {
                        if (!loading) {
                            handleMoveTask(index);
                        }
                    }}
                >
                    <Icon i="arrowup" />
                </ArrowButtonDown>
            )}{" "}
            {removeTaskModal && (
                <Modal close={() => {
                    setRemoveTaskModal(false);
                }}>
                    <Form disabled={loading}>
                        <Title>
                            <FormattedMessage {...messages.deleteTaskModalTitle} />
                        </Title>
                        <p>
                            <FormattedMessage {...messages.deleteTaskModalAreYouSure} />
                        </p>
                        <Submit onClick={handleRemoveTask} loading={loading}>
                            <FormattedMessage {...messages.delete} />
                        </Submit>
                        <Cancel onClick={() => {
                            setRemoveTaskModal(false);
                        }}>
                            <FormattedMessage {...messages.cancel} />
                        </Cancel>
                    </Form>
                </Modal>
            )}
            {editDescription && (
                <Modal close={() => {
                    setEditDescription(false);
                    setIsHover(false);
                }}>
                    <Form disabled={loading}>
                        <Title>
                            {formatCase(formatMessage(messages.modifySomething, { value: formatMessage(messages.description) }))}
                        </Title>
                        <InputTextArea
                            value={description}
                            label={formatMessage(messages.description)}
                            onChange={handleChangeDescription}
                            disabled={publicView}
                            selectAllOnMount
                            maxLength={255}
                        />
                        <Submit onClick={handleSaveDescription} loading={loading} disabled={publicView}>
                            <FormattedMessage {...messages.save} />
                        </Submit>
                        <Cancel onClick={() => {
                            setEditDescription(false);
                            setIsHover(false);
                        }}>
                            <FormattedMessage {...messages.cancel} />
                        </Cancel>
                    </Form>
                </Modal>
            )}
        </StyledTaskCard>
    );
}

export default TaskCard;
